import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'
import _ from 'lodash'
import * as pako from 'pako'
import uuid from 'uuid/v4'
import { applyForEntityTypes } from '@/common/utils/entity-type-util'

export function setLastUserMenu (email, companyId, menuId) {
  localStorage[`user_menu::${email}::${companyId}`] = menuId
}

export function getLastUserMenu (email, companyId) {
  return localStorage[`user_menu::${email}::${companyId}`]
}

const getPublishedMenuBlob = locationId => {
  return axios.get('/api/published-config/menus/' + locationId)
    .then(result => result.data)
}

const getCompanyMenus = companyId => {
  return axios.get(`/api/menus/company/${companyId}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getCompanyMenuVersions = (companyId, menuId) => {
  return axios.get(`/api/menus/company/${companyId}/${menuId}/versions`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const getCompanyMenusByVersion = (companyId, menuId, min, max) => {
  return axios.get(`/api/menus/company/${companyId}/${menuId}/from/${min}/to/${max}`)
    .then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

// FIXME Jason, this should be side effects when you remove a category
// const getAllCategories = menu => {
//   const departments = menu.departments
//
//   if (!departments) {
//     return []
//   }
//
//   return _.flatMap(departments, department => department.categories)
// }
//
// function cleanMenu (menu) {
//   if (menu.onlineCategories) {
//     const categories = getAllCategories(menu)
//
//     // Filter out categories that no longer exist
//     menu.onlineCategories.forEach(category => {
//       category.categoryIds = _.filter(category.categoryIds, id => {
//         return _.find(categories, { id })
//       })
//     })
//
//     menu.onlineCategories.sort((a, b) => a.index - b.index)
//   }
// }

function createCompanyMenu (menu, companyId) {
  return axios({
    method: 'POST',
    url: `/api/menus/company/${companyId}`,
    data: menu
  }).then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const updateCompanyMenu = (menu, companyId) => {
  let menuCopy = { ...menu, menuBlob: JSON.stringify(menu.menuBlob) }
  const compressedMenu = pako.gzip(JSON.stringify(menuCopy))
  return axios({
    method: 'PUT',
    url: `/api/menus/company/${companyId}`,
    data: compressedMenu,
    headers: { 'Content-Type': 'application/octet-stream' }
  }).then(res => res.data)
    .catch(error => exceptionHandler.parse(error))
}

const deleteCompanyMenu = (menuId, companyId) => {
  return axios.post(`/api/menus/company/${companyId}/${menuId}/delete`)
    .catch(error => exceptionHandler.parse(error))
}

const saveLocationMenu = (companyId, locationId, overrides, overrideVer, attempt) => {
  return axios({
    method: 'post',
    url: `/api/menus/location/${companyId}/${locationId}`,
    params: { overrideVer },
    data: overrides
  })
    .then(res => res.data)
    .catch(error => exceptionHandler.parseWithRetryWhenConflict(error, attempt,
      next => saveLocationMenu(companyId, locationId, overrides, overrideVer, next)))
}

const getSignedUrl = ({ companyId, fileName, contentType }) => {
  return axios.get('/api/menus/image-upload-url/' + companyId, {
    params: { fileName, contentType }
  }).then(result => {
    return result.data
  })
}

const uploadFile = ({ companyId, fileName, contentType, file }) => {
  return getSignedUrl({ companyId, fileName, contentType }).then(result => {
    return axios.put(result.signedUrl, file, {
      headers: { 'Content-Type': contentType, 'Cache-Control': result.cacheControl }
    }).then(() => {
      return result.tccUrl
    })
  }).catch(error => exceptionHandler.parse(error))
}

export default {
  getTemplateMenus () {
    return axios.get('/api/menus/templates')
      .then(result => result.data)
  },
  getTemplate (templateId) {
    return getMenu(`/api/menus/templates/${templateId}`)
  },
  getCompanyMenu (companyId, menuId, expectedVersion) {
    return getMenu(`/api/menus/company/${companyId}/${menuId}`, expectedVersion)
  },
  getCompanyMenus,
  getCompanyMenuVersions,
  getCompanyMenusByVersion,
  getPublishedMenuBlob,
  createCompanyMenu,
  updateCompanyMenu,
  deleteCompanyMenu,
  getLocationMenu (companyId, locationId, expected) {
    return axios.get(`/api/menus/location/${companyId}/${locationId}`, { params: expected })
      .then(res => {
        let data = res.data

        if (!data.menuBlob && !data.override) {
          return 304
        }

        let base = data
        let diff = null
        let groupDiff = null
        if (!base.override) {
          if (!expected.expectedOverrideVer) {
            diff = { changes: [] }
          }
        } else {
          diff = JSON.parse(base.override)
        }

        if (base.groupOverride) {
          groupDiff = JSON.parse(base.groupOverride)
        }

        delete base.override
        delete base.groupOverride

        if (base.menuBlob) {
          base.menuBlob = JSON.parse(base.menuBlob)
        }

        return { base, diff, groupDiff }
      })
      .catch(error => exceptionHandler.parse(error))
  },
  saveLocationMenu,
  uploadFile,
  emptyMenu () {
    const menu = {
      id: uuid(),
      name: '',
      desc: '',
      template: false
    }

    const menuBlob = {
      pluNumbers: {
        entityType: 'PLU_NUMBERS'
      },
      taxStructures: [
        {
          id: uuid(),
          entityType: 'TAX_STRUCTURE',
          name: 'Sales Tax',
          applies: 'AFTER',
          flatRate: '0',
          byOrderType: false,
          orderTypeRates: [],
          defaultOrderType: null,
          settings: []
        }
      ],
      pricingSchemes: [
        {
          entityType: 'PRICING_SCHEME',
          id: uuid(),
          name: 'Default',
          description: 'Default Pricing Scheme',
          isDefault: true
        }
      ]
    }
    menu.menuBlob = JSON.stringify(menuBlob)

    return menu
  }
}

function getMenu (url, expectedVersion) {
  return axios.get(url, { params: { ...(expectedVersion ? { expectedVersion } : {}) } })
    .then(res => {
      let menu = res.data
      menu.menuBlob = JSON.parse(menu.menuBlob)
      return menu
    })
    .catch(error => {
      if (_.get(error, 'response.status') === 304) {
        return 304
      }
      return exceptionHandler.parse(error)
    })
}

export function getPluNumber (menuBlob, entityType, id) {
  if (!menuBlob.hasOwnProperty('pluNumbers')) {
    menuBlob.pluNumbers = { entityType: 'PLU_NUMBERS' }
  }

  const usedPluNumbers = getUsedPluNumbers(menuBlob, entityType, id)
  const highestCurrentPlu = Math.max(1000, ...usedPluNumbers)

  const recordedPlu = menuBlob.pluNumbers.hasOwnProperty(entityType)
    ? menuBlob.pluNumbers[entityType]
    : 0

  const nextNumber = Math.max(recordedPlu, highestCurrentPlu + 1)

  menuBlob.pluNumbers[entityType] = nextNumber + 1
  return nextNumber
}

export function getUsedPluNumbers (menuBlob, entityType, id) {
  const usedPluNumbers = []
  const collect = info => {
    if (info && info.id !== id && info.pluNum) {
      usedPluNumbers.push(parseInt(info.pluNum))
    }
  }

  // Use structural position rather than entityType field so that blobs with
  // corrupted/missing entityType values on info objects are still counted.
  switch (entityType) {
    case 'DEPARTMENT_INFO':
      for (const dept of (menuBlob.departments || [])) {
        collect(dept.info)
      }
      break

    case 'MODIFIER_ITEM_INFO':
    case 'DEPARTMENT_ITEM_INFO':
      for (const dept of (menuBlob.departments || [])) {
        for (const item of (dept.items || [])) {
          collect(item.info)
        }
      }
      for (const item of (_.get(menuBlob, 'modifiers.items', []))) {
        collect(item.info)
      }
      break

    case 'MODIFIER_CATEGORY_INFO':
    case 'DEPARTMENT_CATEGORY_INFO':
      for (const dept of (menuBlob.departments || [])) {
        for (const cat of (dept.categories || [])) {
          collect(cat.info)
        }
      }
      for (const cat of (_.get(menuBlob, 'modifiers.categories', []))) {
        collect(cat.info)
      }
      break

    default: {
      const func = obj => {
        if (obj.id !== id && obj.pluNum) {
          usedPluNumbers.push(parseInt(obj.pluNum))
        }
      }
      applyForEntityTypes(menuBlob, [entityType], func)
    }
  }

  return usedPluNumbers
}

export function isPluUnique (menuBlob, plu, entityType, id) {
  if (!plu || plu === '') {
    return true
  }
  return !getUsedPluNumbers(menuBlob, entityType, id).includes(plu)
}
